"use client";

import { useActionState } from "react";
import { applyAction, type ApplyState } from "./actions";

/*
 * The application form for both practitioner pages.
 *
 * Confirmation deliberately does not say whether the address was already on
 * file: someone re-applying and someone applying fresh see the same thing,
 * which keeps the form from confirming who is already in the network.
 */
export function ApplyForm({
  kind, focusLabel, focusHint, statementLabel, statementHint, submitLabel,
}: {
  kind: "consultant" | "trainee";
  focusLabel: string; focusHint?: string;
  statementLabel: string; statementHint?: string;
  /** The design gives each page its own button wording -- "Create your
   *  consultant account" and "Apply as a trainee" -- because the two are
   *  different commitments and a shared "Submit" says neither. */
  submitLabel: string;
}) {
  const [state, action, pending] = useActionState<ApplyState, FormData>(applyAction, {});

  if (state.done) {
    return (
      <div style={{
        padding: "18px 20px", fontSize: 15, lineHeight: 1.65,
        background: "var(--color-accent-100)", color: "var(--color-accent-800)",
        border: "1px solid var(--color-accent-400)",
      }}>
        Thanks — that is with us. We read every one and come back within a week,
        either way.
      </div>
    );
  }

  return (
    <form action={action} noValidate>
      <input type="hidden" name="kind" value={kind} />

      <div className="field" style={{ marginBottom: 14 }}>
        <label htmlFor="name">Name</label>
        <input className="input" id="name" name="name" autoComplete="name" required />
      </div>

      <div className="field" style={{ marginBottom: 14 }}>
        <label htmlFor="focus">{focusLabel}</label>
        {focusHint && <p className="text-muted" style={{ fontSize: 12.5, margin: "0 0 6px" }}>{focusHint}</p>}
        <input className="input" id="focus" name="focus" required />
      </div>

      <div className="field" style={{ marginBottom: 14 }}>
        <label htmlFor="email">Email</label>
        <input className="input" id="email" name="email" type="email" autoComplete="email" required />
      </div>

      <div className="field" style={{ marginBottom: 14 }}>
        <label htmlFor="links">Portfolio, GitHub or LinkedIn</label>
        <input className="input" id="links" name="links" placeholder="Anything we can actually check" />
      </div>

      <div className="field" style={{ marginBottom: 14 }}>
        <label htmlFor="statement">{statementLabel}</label>
        {statementHint && <p className="text-muted" style={{ fontSize: 12.5, margin: "0 0 6px" }}>{statementHint}</p>}
        <textarea className="input" id="statement" name="statement" rows={5} required />
      </div>

      {state.error && (
        <p role="alert" style={{
          fontSize: 13, marginBottom: 12, padding: "8px 10px",
          color: "var(--color-danger-900)", background: "var(--color-danger-100)",
          border: "1px solid var(--color-danger-500)",
        }}>{state.error}</p>
      )}

      <button className="btn btn-primary btn-block blueprint" type="submit" disabled={pending}
              style={{ padding: "14px 24px", fontSize: 16 }}>
        <i className="corner tl" aria-hidden="true" /><i className="corner tr" aria-hidden="true" />
        <i className="corner bl" aria-hidden="true" /><i className="corner br" aria-hidden="true" />
        {pending ? "Sending…" : submitLabel}
      </button>
    </form>
  );
}
